import {
  SplitButton,
  SplitButtonItem,
  SplitButtonItemClickEvent,
} from '@progress/kendo-react-buttons';
import { GridCellProps } from '@progress/kendo-react-grid';
import { useCallback, useMemo, useState } from 'react';
import GridCellBase from 'src/components/GridCellBase';
import Button from 'src/components/design-system/Button';
import useViewCheckbook from 'src/hooks/useViewCheckbook';
import useYearView from 'src/hooks/useYearView';
import { GLCodesBudgetByPeriodBudgets } from 'src/services/useGlCodesBudgetByYearApi';

const appendIndex = (str: 'initialBudgetPeriod' | 'budgetPeriod', num: number) =>
  `${str}${String(num).padStart(2, '0')}`;

const CopyAcrossCell = (props: GridCellProps) => {
  const {
    setEditingGlCodeYearBudget,
    dropDownYearValue,
    year,
    isEditMode,
    hasManageBudgetPermission,
    hasUsesInitialBudgetsPermission,
    hasCanOverrideBudgetsPermission,
  } = useViewCheckbook();
  const [isOpened, setIsOpened] = useState(false);
  const { dataItem } = props;
  const selectedYear = dropDownYearValue?.year;
  const isPastYear = useMemo(
    () => selectedYear !== undefined && selectedYear < year,
    [selectedYear, year]
  );
  const { editableColumnDropdownData } = useYearView();

  const handleButtonClick = useCallback(() => {
    setIsOpened((prev) => !prev);
  }, []);

  const { isBudgetVisible, isForecastVisible } = useMemo(() => {
    const isBudgetVisible = editableColumnDropdownData.some(
      (el) => el.show && el.field === 'initialBudgetPeriod'
    );
    const isForecastVisible = editableColumnDropdownData.some(
      (el) => el.show && el.field === 'budgetPeriod'
    );
    return {
      isBudgetVisible,
      isForecastVisible,
    };
  }, [editableColumnDropdownData]);

  const handleCopyAcross = useCallback(
    (option: string) => {
      const isBudget = option.toLowerCase().includes('budget');
      const isForecast = option.toLowerCase().includes('forecast');

      setEditingGlCodeYearBudget((prev: any) => {
        const yearBudget = [...prev];
        const index = yearBudget.findIndex((item) => item.rowId === dataItem.rowId);
        const isCurrentYear = selectedYear === year;
        let item = { ...yearBudget[index] };
        let currentPeriodIndex = 0;
        if (isCurrentYear) {
          currentPeriodIndex = item.periodBudgets.findIndex(
            (period: GLCodesBudgetByPeriodBudgets) => {
              const startDate = new Date(period.startDate);
              const endDate = new Date(period.endDate);
              return startDate <= new Date() && new Date() <= endDate;
            }
          );
        }
        const budgetValue = item[appendIndex('initialBudgetPeriod', currentPeriodIndex + 1)];
        const forecastValue = item[appendIndex('budgetPeriod', currentPeriodIndex + 1)];
        for (let i = currentPeriodIndex; i < item.periodBudgets.length; i++) {
          if (isBudget) {
            const isBudgetEditable =
              isEditMode && !item.isDeleted && hasManageBudgetPermission && dropDownYearValue
                ? hasCanOverrideBudgetsPermission
                  ? true
                  : item[`_${appendIndex('initialBudgetPeriod', i + 1)}`] === null
                : false;

            if (isBudgetEditable && budgetValue !== null) {
              item.periodBudgets[i].initialBudget = budgetValue;
              item[appendIndex('initialBudgetPeriod', i + 1)] = budgetValue;
            }
          }
          if (isForecast) {
            const isForecastEditable =
              isEditMode && hasManageBudgetPermission && dropDownYearValue && !item.isDeleted;
            if (isForecastEditable) {
              item.periodBudgets[i].totalBudgetAmount = forecastValue;
              item[appendIndex('budgetPeriod', i + 1)] = forecastValue;
            }
          }
        }

        yearBudget[index] = item;
        return yearBudget;
      });
    },
    [
      dataItem.rowId,
      dropDownYearValue,
      hasCanOverrideBudgetsPermission,
      hasManageBudgetPermission,
      isEditMode,
      selectedYear,
      setEditingGlCodeYearBudget,
      year,
    ]
  );

  const handleItemClick = useCallback(
    (event: SplitButtonItemClickEvent) => {
      const selectedItem = event.item.text;
      handleCopyAcross(selectedItem);
      setIsOpened(false);
    },
    [handleCopyAcross]
  );

  const getTitleMessage = (baseMessage: string) =>
    isBudgetVisible && isForecastVisible
      ? `${baseMessage} Forecast Amount and Budget Amount`
      : !isBudgetVisible && !isForecastVisible
      ? ``
      : !isBudgetVisible || !hasUsesInitialBudgetsPermission
      ? `${baseMessage} Forecast Amount`
      : !isForecastVisible
      ? `${baseMessage} Budget Amount`
      : '';

  const title = isPastYear
    ? getTitleMessage('Must not be in a past year to edit')
    : dataItem && dataItem.isDeleted
    ? getTitleMessage('Must be an Undeleted code to edit')
    : '';

  const isDisabled =
    isPastYear ||
    !hasManageBudgetPermission ||
    dataItem.isDeleted ||
    (!isBudgetVisible && !isForecastVisible);

  const itemRender = (props: any) => <p className="text-sm">{props.text}</p>;
  return (
    <GridCellBase
      {...props}
      title={title}
      rowSelectionEnabled={false}
      className={` ${isEditMode ? '!text-center' : ''} ${props.className}`}
    >
      {hasUsesInitialBudgetsPermission ? (
        <SplitButton
          fillMode={'flat'}
          className={`   
            ${
              isDisabled
                ? 'text-disabledtext text-center cursor-not-allowed bg-disabled'
                : 'bg-info text-white'
            }`}
          buttonClass="split-main-button"
          text="Copy Across"
          size="small"
          onButtonClick={handleButtonClick}
          onItemClick={handleItemClick}
          opened={isOpened}
          onClose={() => {
            setIsOpened(false);
          }}
          onOpen={() => {
            setIsOpened(true);
          }}
          disabled={isDisabled}
          style={{ borderRadius: 0 }}
          tabIndex={0}
        >
          <SplitButtonItem
            text="Budget and Forecast"
            disabled={
              !isBudgetVisible || !isForecastVisible || !dataItem?.isBudgetCopyAcrossEnabled
            }
            render={() => itemRender({ text: 'Budget and Forecast' })}
          />
          <SplitButtonItem
            text="Only Budget "
            disabled={!isBudgetVisible || !dataItem?.isBudgetCopyAcrossEnabled}
            render={() => itemRender({ text: 'Only Budget' })}
          />
          <SplitButtonItem
            text="Only Forecast"
            disabled={!isForecastVisible}
            render={() => itemRender({ text: 'Only Forecast' })}
          />
        </SplitButton>
      ) : (
        <Button
          size="sm"
          color="info"
          onClick={() => handleCopyAcross('Only Forecast')}
          disabled={isPastYear || !hasManageBudgetPermission || dataItem.isDeleted}
        >
          Copy Across
        </Button>
      )}
    </GridCellBase>
  );
};

export default CopyAcrossCell;
